const About = ({ successHandler }) => {
  const teamMembers = [
    {
      name: "Dr. Inna Tolskaya",
      role: "Product & Linguistics",
      image: "it.jpg",
      expertise:
        "Linguist, project manager, and software developer with experience in language technology and e-learning.",
      projects: [
        {
          name: "EasyRights",
          url: "http://www.easyrights.polimi.it/",
          description:
            "A platform helping immigrants understand and use their rights with language support.",
        },
        {
          name: "Capeesh",
          url: "https://www.capeesh.com/",
          description:
            "Interactive language-learning app with courses and pronunciation practice.",
        },
      ],
    },
    {
      name: "Associate professor Olaf Husby",
      role: "Teaching & Didactics",
      image: "oh.jpg",
      expertise:
        "Pronunciation specialist, second language educator, course developer, and Norwegian dialect expert.",
      projects: [
        {
          name: "NOW",
          url: "https://www.ntnu.edu/now/",
          description:
            "Free beginner Norwegian course with texts, audio, and exercises.",
        },
        {
          name: "NOW2",
          url: "https://www.ntnu.edu/now2",
          description:
            "Intermediate Norwegian course for learners with previous knowledge.",
        },
        {
          name: "CALST",
          url: "https://www.ntnu.edu/isl/calst",
          description:
            "Pronunciation training platform with listening and speaking exercises.",
        },
        {
          name: "L1-L2 Map",
          url: "https://l1-l2map.it.ntnu.no/",
          description:
            "Tool comparing sounds across languages to show what learners may need to practise.",
        },
      ],
    },
    {
      name: "Dr. Dmitry Shcherbin",
      role: "Technology & Research",
      image: "ds.jpg",
      expertise:
        "Software developer, data analyst, and researcher focused on practical language-learning tools.",
      projects: [
        {
          name: "CALST",
          url: "https://www.ntnu.edu/isl/calst",
          description:
            "Research-based pronunciation training system for language learners.",
        },
        {
          name: "Pygmalion",
          url: "https://pygmalion.hf.ntnu.no/",
          description:
            "Research prototype for multilingual pronunciation training.",
        },
        {
          name: "EasyRights",
          url: "http://www.easyrights.polimi.it/",
          description:
            "Platform helping immigrants understand their rights.",
        },
      ],
    },
  ];

  const principles = [
    {
      title: "Real speech",
      text: "Practice starts from spoken texts, rhythm, phrasing, and natural models.",
    },
    {
      title: "Focused repetition",
      text: "Learners repeat, compare, notice, and gradually gain more control.",
    },
    {
      title: "Human feedback",
      text: "Sometimes the most useful help is another person listening carefully.",
    },
  ];

  const accountFeatures = [
    "progress",
    "statistics",
    "recordings",
    "personal drills",
    "feedback requests",
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {successHandler && (
          <button
            type="button"
            onClick={() => successHandler(false)}
            className="mb-5 inline-flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50"
          >
            ← Back
          </button>
        )}

        <main className="overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-sm">
          <section className="bg-gradient-to-br from-white via-blue-50/45 to-slate-50 px-6 py-12 md:px-10 md:py-16">
            <div className="mx-auto max-w-4xl text-center">
              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-600">
                About Catchphraser
              </div>

              <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
                Pronunciation is practice.
              </h1>

              <p className="mx-auto mt-5 max-w-3xl text-base font-semibold leading-7 text-slate-600 md:text-lg md:leading-8">
                Catchphraser helps learners move from knowing words to speaking
                more clearly. The aim is not a perfect accent, but speech that
                feels easier to produce and easier for others to understand.
              </p>
            </div>
          </section>

          <section className="border-t border-slate-200 px-6 py-10 md:px-10">
            <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                  Why
                </div>

                <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                  Speech is more than vocabulary.
                </h2>

                <p className="mt-4 text-base font-semibold leading-7 text-slate-600">
                  In conversation, small details matter: timing, stress, vowel
                  quality, consonants, phrasing, and intonation. Catchphraser
                  gives learners a place to slow these details down and practise
                  them safely.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {principles.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[24px] border border-slate-200 bg-slate-50/70 p-5"
                  >
                    <h3 className="text-base font-black text-slate-950">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="border-t border-slate-200 bg-slate-50/70 px-6 py-10 md:px-10">
            <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                  How
                </div>

                <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                  Listen, repeat, notice, adjust.
                </h2>

                <p className="mt-4 text-base font-semibold leading-7 text-slate-600">
                  Learners work with model audio, short phrases, visual cues,
                  and recordings. Some practice is slow and careful. Some is
                  closer to real speech rhythm.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-[24px] border border-white bg-white p-5 shadow-sm">
                  <div className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700">
                    Shadowing
                  </div>
                  <h3 className="mt-2 text-xl font-black text-slate-950">
                    Follow the flow
                  </h3>
                  <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                    Speak along with the model voice to train timing, rhythm,
                    and intonation.
                  </p>
                </div>

                <div className="rounded-[24px] border border-white bg-white p-5 shadow-sm">
                  <div className="text-[11px] font-black uppercase tracking-[0.18em] text-violet-700">
                    Echoing
                  </div>
                  <h3 className="mt-2 text-xl font-black text-slate-950">
                    Repeat with care
                  </h3>
                  <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                    Pause after a phrase and repeat it with attention to sound
                    and articulation.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="border-t border-slate-200 px-6 py-10 md:px-10">
            <div className="rounded-[30px] border border-blue-100 bg-blue-50/50 p-6 md:p-8">
              <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.18em] text-blue-700">
                    Personal practice
                  </div>

                  <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                    Sign in when you want Catchphraser to remember you.
                  </h2>

                  <p className="mt-4 text-base font-semibold leading-7 text-slate-600">
                    The library is open to explore. An account adds continuity:
                    it keeps your work and helps you decide what to practise
                    next.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {accountFeatures.map((feature) => (
                    <span
                      key={feature}
                      className="rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="border-t border-slate-200 px-6 py-10 md:px-10">
            <div className="mx-auto max-w-4xl text-center">
              <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                Team
              </div>

              <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                Built by people working with language, teaching, and software.
              </h2>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-3">
              {teamMembers.map((member) => (
                <article
                  key={member.name}
                  className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                      <img
                        src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/${member.image}`}
                        alt={member.name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>

                    <div>
                      <h3 className="text-lg font-black leading-tight text-slate-950">
                        {member.name}
                      </h3>
                      <p className="mt-1 text-sm font-bold text-blue-700">
                        {member.role}
                      </p>
                    </div>
                  </div>

                  <p className="mt-5 text-sm font-semibold leading-6 text-slate-600">
                    {member.expertise}
                  </p>

                  <div className="mt-5 border-t border-slate-100 pt-4">
                    <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                      Selected work
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {member.projects.map((project) => (
                        <a
                          key={project.name}
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={project.description}
                          className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                        >
                          {project.name}
                        </a>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="border-t border-slate-200 bg-slate-950 px-6 py-10 text-center md:px-10">
            <p className="mx-auto max-w-3xl text-xl font-black leading-8 text-white md:text-2xl md:leading-9">
              The goal is clear, confident communication — not perfection.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default About;